import { BffUnauthenticatedError, bffFetch, bffJson, type ReadLane } from "@/shared/api/client";

export interface IndividualWire {
  id: string;
  [field: string]: unknown;
}

export function readIndividualDirectory(): Promise<ReadonlyMap<string, IndividualWire> | null> {
  const held = heldIndividualDirectory();
  if (held) return held;
  const entry: Kept = {
    at: Date.now(),
    read: load(),
    settled: null,
  };
  kept = entry;
  void entry.read.then((directory) => {
    entry.settled = directory;
    if (directory === null && kept === entry) kept = null;
  });
  return entry.read;
}

export function settledIndividualDirectory(): ReadonlyMap<string, IndividualWire> | null {
  return heldIndividualDirectory() ? (kept?.settled ?? null) : null;
}

function heldIndividualDirectory(): Promise<ReadonlyMap<string, IndividualWire> | null> | null {
  return kept && Date.now() - kept.at < DIRECTORY_TTL_MS ? kept.read : null;
}

export function invalidateIndividualDirectory(): void {
  kept = null;
  reads.clear();
}

export interface IndividualRead {
  status: number;
  text: string;
}

export function readIndividualOnce(
  individualId: string,
  lane: Pick<ReadLane, "background"> = {},
): Promise<IndividualRead> {
  const held = reads.get(individualId);
  if (held && (held.settledAt === null || Date.now() - held.settledAt < READ_REUSE_MS)) {
    return held.read;
  }
  const entry: HeldRead = { settledAt: null, read: fetchIndividual(individualId, lane) };
  reads.set(individualId, entry);
  entry.read.then(
    () => {
      entry.settledAt = Date.now();
    },
    () => {
      if (reads.get(individualId) === entry) reads.delete(individualId);
    },
  );
  return entry.read;
}

async function fetchIndividual(
  individualId: string,
  lane: Pick<ReadLane, "background">,
): Promise<IndividualRead> {
  let response: Response;
  try {
    response = await bffFetch(`v1/individual/${encodeURIComponent(individualId)}`, {
      background: lane.background,
    });
  } catch (err) {
    if (err instanceof BffUnauthenticatedError) throw err;
    return { status: 0, text: "" };
  }
  return { status: response.status, text: await response.text().catch(() => "") };
}

async function load(): Promise<ReadonlyMap<string, IndividualWire> | null> {
  try {
    const rows = await bffJson<unknown>("v1/individual");
    if (!Array.isArray(rows)) return null;
    return new Map(
      rows
        .filter((row): row is IndividualWire => typeof row?.id === "string" && row.id !== "")
        .map((row) => [row.id, row]),
    );
  } catch (err) {
    if (err instanceof BffUnauthenticatedError) throw err;
    console.warn(
      "individual directory: GET /v1/individual failed — reading persons one by one:",
      err instanceof Error ? err.message : "unknown",
    );
    return null;
  }
}

interface HeldRead {
  settledAt: number | null;
  read: Promise<IndividualRead>;
}

interface Kept {
  at: number;
  read: Promise<ReadonlyMap<string, IndividualWire> | null>;
  settled: ReadonlyMap<string, IndividualWire> | null;
}

let kept: Kept | null = null;

const reads = new Map<string, HeldRead>();

const DIRECTORY_TTL_MS = 5 * 60_000;

const READ_REUSE_MS = 5_000;
